import * as React from "react"

import { Data } from "effect"

export interface Analytics {
  identify: (user: Analytics.User) => void

  reset: () => void

  track: (event: Analytics.Event) => void

  track_links: (query: string, event: Analytics.Event) => void
}

export namespace Analytics {
  export type User = {
    id: string
    email: string
    name: string
    authMethod: string
    createdAt: string
  }

  export const EventNames: Record<Event["_tag"], string> = {
    // Check-In
    CheckInStarted: "Check-In: Started",
    CheckInPhoneNumberSubmitted: "Check-In: Phone Number Submitted",
    CheckInConfirmed: "Check-In: Confirmed",
    LoginSubmitted: "Login: Submitted",
    LoginVerifySubmitted: "Login Verify: Submitted",
    LoginResendOtpSubmitted: "Login Resend OTP: Submitted",
    OnboardingSubmitted: "Onboarding: Submitted",
    MerchTapLanded: "Merch Tap: Landed",
    MerchTapSkippedClaim: "Merch Tap: Skipped Claim",
    MerchTapStarted: "Merch Tap: Started Claim",
    MerchTapConfirmed: "Merch Tap: Confirmed",
    MerchBuyNowClicked: "Merch Buy Now: Clicked",
    ExclusiveViewed: "Exclusives: Viewed",
    ShowViewed: "Show: Viewed",
    CheckInGpsRejected: "Check-In: GPS permission rejected",
    CheckInLocationInvalid: "Check-In: Invalid location",
    ExclusiveVideoPlayed: "Exclusive Video: Played",
    TrackPlayed: "Track: Played",
    TrackSkipped: "Track: Skipped",
    TrackEnded: "Track: Ended",
    HowToTapViewed: "How to Tap: Viewed",
    JoinProofLanded: "Join Proof: Landed",
    JoinProofStarted: "Join Proof: Started Claim",
    JoinProofConfirmed: "Join Proof: Confirmed",
    PlaylistDownloadLinkRequested: "Playlist: Download Link Requested",
    PlaylistDownloadClicked: "Playlist: Download Clicked",
    PlaylistDownloaded: "Playlist: Downloaded",
    ShopCardClicked: "Shop Card: Clicked",
    LineAddedToCart: "Cart: Line Added",
    LineRemovedFromCart: "Cart: Line Removed",
    LineQuantityUpdated: "Cart: Line Quantity Updated",
    CheckoutStarted: "Checkout: Started",
    CdPlaylistViewed: "CD Playlist: Viewed",
    FanClipViewed: "Fan Clip: Viewed",
    FanClipShared: "Fan Clip: Shared"
  }

  export type Event = Data.TaggedEnum<{
    CheckInStarted: { showId: string }
    CheckInPhoneNumberSubmitted: { showId: string }
    CheckInConfirmed: { showId: string }
    LoginSubmitted: { method: "google" | "email" }
    LoginVerifySubmitted: {}
    LoginResendOtpSubmitted: {}
    OnboardingSubmitted: { isArtistPrivacyPolicyChecked?: boolean }
    MerchTapLanded: {
      merchSkuId: string
      merchItemId: string
      status: "Unclaimed" | "Claimed"
    }
    MerchTapSkippedClaim: { merchSkuId: string; merchItemId: string }
    MerchTapStarted: { merchSkuId: string; merchItemId: string }
    MerchTapConfirmed: { merchSkuId: string; merchItemId: string }
    MerchBuyNowClicked: { merchSkuId: string }
    ExclusiveViewed: { exclusiveId: string }
    ShowViewed: { showId: string; showDate: string }
    CheckInGpsRejected: { error: string }
    CheckInLocationInvalid: { showId: string }
    ExclusiveVideoPlayed: { exclusiveId: string }
    TrackPlayed: { trackId: string; context?: "playlist" | "auto" | "manual" | "skip" }
    TrackSkipped: { trackId: string }
    TrackEnded: { trackId: string }
    HowToTapViewed: {}
    JoinProofLanded: {}
    JoinProofStarted: {}
    JoinProofConfirmed: {}
    PlaylistDownloadLinkRequested: { playlistId: string; email: string }
    PlaylistDownloadClicked: { playlistId: string }
    PlaylistDownloaded: { playlistId: string }
    ShopCardClicked: {}
    LineAddedToCart: { productVariantId: string; quantity: number }
    LineRemovedFromCart: { productVariantId: string }
    LineQuantityUpdated: { productVariantId: string; quantity: number }
    CheckoutStarted: {}
    CdPlaylistViewed: { playlistId: string }
    FanClipViewed: { fanClipId: string; source?: "share_link" }
    FanClipShared: { fanClipId: string }
  }>

  export const Event = Data.taggedEnum<Event>()
}

export const AnalyticsContext = React.createContext<Analytics | null>(null)

export const consoleAnalytics: Analytics = {
  identify: (user) => console.log("Analytics.identify", user),
  reset: () => console.log("Analytics.reset"),
  track: (event) => {
    const { _tag, ...rest } = event
    console.log("Analytics.track", { event: Analytics.EventNames[_tag], ...rest })
  },
  track_links: (query, event) => {
    const { _tag, ...rest } = event
    console.log("Analytics.track_links", {
      query,
      event: Analytics.EventNames[_tag],
      ...rest
    })
  }
}

export const useAnalytics = () => {
  const analytics = React.useContext(AnalyticsContext)

  if (!analytics) {
    throw new Error("useAnalytics must be used within a AnalyticsProvider")
  }

  return analytics
}
